<template lang="pug">
  VContainer.sale__container
    .sale__supply
      span.t--bold {{ totalMinted }} / {{ getIOContractData.maxSupply }}&nbsp;
      span IO Minted

    h1.sale__title(
        tabindex="0"
      )

        template(v-if="isPreSaleLive && !isWhitelisted")
          span You Are Not
          span.t--yellow.inline Whitelisted
          .mint-details__container
            div
              span.no-bold Public Dutch Auction Mint Begins On&nbsp;
              span {{ publicSaleLocalTime }}
            VBtn.v-btn--home(
              x-large
              rounded
              dark
              :elevation="0"
              :href="homeUrl"
            ) Back to Home
        template(v-else)
          span Mint Your
          span.t--yellow.inline Imaginary Ones

    .mint__container
      template(v-if="showPreSaleComponent || isPublicSaleLive")
        .sale__quantity(v-if="showPreSaleComponent")
          VIcon.minus(
            :class="{ 'is-disabled': mintQuantity - 1 <= 0}"
            @click="decreaseQuantity"
          ) icon-caret
          .quantity {{ mintQuantity }}
          VIcon.plus(
            :class="{ 'is-disabled': mintQuantity + 1 > mintBalance}"
            @click="increaseQuantity"
          ) icon-caret
        VBtn.v-btn--mint(
          x-large
          rounded
          dark
          :elevation="0"
          :disabled="showReCAPTCHAV2 && reCAPTCHAV2Token === ''"
          @click="mint"
        ) Mint

        template(v-if="showReCAPTCHAV2")
          .recaptcha-v2
            label.recaptcha-v2__error
              | Please verify that you are human:
            VueRecaptcha(
              :sitekey="reCAPTCHAV2SiteKey"
              :loadRecaptchaScript="true"
              ref="recaptchaV2"
              @verify="onReCAPTCHAV2Verified"
              @expired="onReCAPTCHAV2Expired"
            )

    .sale__price
      template(v-if="this.showPreSaleComponent")
        span Total : {{ price }} ETH
      template(v-else-if="isPublicSaleLive")
        div
          span Current Price:&nbsp;
          span.t--regular {{ daPrice }} ETH
        div
          span Price Drops:&nbsp;
          span.t--regular {{ getIOContractData.publicSaleDropAmount }} ETH every {{ getIOContractData.publicSaleDropInterval / 60 }} minutes until {{ getIOContractData.floorPrice }} ETH

    .sale__preloader(v-if="isMinting")


</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex';
import axios from 'axios';
import exactMath from 'exact-math';
import { ethers } from 'ethers';
import { MerkleTree } from 'merkletreejs';

import saleMixin from '@/mixins/sale-mixins';

import ErrorDict from '@/assets/data/error.json';

import { VueRecaptcha } from 'vue-recaptcha';

// NOTE: import { keccak256 } from 'keccak256'; DOESNT WORK THE SAME AS const keccak256 = require('keccak256');
const keccak256 = require('keccak256');

export default {
  name: 'MintSale',
  components: { VueRecaptcha },
  mixins: [saleMixin],
  data() {
    return {
      mintQuantity: 1,
      isMinting: true,
      reCAPTCHAV2Token: '',
      showReCAPTCHAV2: false,
      daPrice: 1.5,
    };
  },
  computed: {
    ...mapGetters('contracts', [
      'getIOAbi',
      'getIOContract',
      'getIOContractData',
      'getIsNewDAInterval',
      'getDAData',
    ]),
    ...mapGetters('accounts', ['getAccountData', 'getActiveAccountChecksum', 'getProviderEthers']),
    homeUrl() {
      return process.env.VUE_APP_LANDING_URL;
    },
    isWhitelisted() {
      return this.getAccountData.isWhitelisted;
    },
    price() {
      if (this.isPreSaleLive) {
        return exactMath.mul(parseFloat(this.getIOContractData.floorPrice), this.mintQuantity);
      }

      return 888;
    },
    mintBalance() {
      return this.getAccountData.allowedMintQuantity - this.getAccountData.mintedQuantity;
    },
    totalMinted() {
      return this.getIOContractData.maxSupply - this.getIOContractData.remainingSupply;
    },
    showPreSaleComponent() {
      return this.isPreSaleLive && this.isWhitelisted;
    },
    reCAPTCHAV2SiteKey() {
      return process.env.VUE_APP_RECAPTCHA_V2_SITE_KEY;
    },
    mintUnsuccessfulReCAPTCHAText() {
      return 'Unable to process reCAPTCHA for mint';
    },
  },
  methods: {
    ...mapMutations('accounts', ['setIsMinting']),
    ...mapMutations('contracts', ['setIsNewDAInterval']),
    ...mapActions('contracts', ['storeDAData']),
    notifyError(text) {
      this.$root.$emit('notify', {
        type: 'error',
        text: text,
      });
    },

    decreaseQuantity() {
      if (this.mintQuantity - 1 > 0) this.mintQuantity--;
    },
    increaseQuantity() {
      if (
        this.mintQuantity + 1 <=
        this.getAccountData.allowedMintQuantity - this.getAccountData.mintedQuantity
      )
        this.mintQuantity++;
    },
    hashLeaf(address, allowedMintQty) {
      const hash = Buffer.from(
        ethers.utils.solidityKeccak256(['address', 'uint256'], [address, allowedMintQty]).slice(2),
        'hex'
      );
      return hash;
    },
    generateMerkleTree() {
      // Hash all whitelisted addresses and allowed mint quantity as the leafNode in MerkleTree
      const leafNodes = Object.entries(this.$getWl()).map((whitelistee) =>
        this.hashLeaf(...whitelistee)
      );
      // Create merkle tree
      const merkleTree = new MerkleTree(leafNodes, keccak256, { sortPairs: true });

      // Return merkleRoot, it is required to set the merkle root on the SC side!
      // NOTE: Need to add '0x' infront of the merkleroot before setting merkle root on SC side
      // Return merkleTree, it is required to verify the merkle proof later
      // Return leafNodes, it is required to verify the merkle proof later
      return {
        merkleRoot: merkleTree.getRoot().toString('hex'),
        merkleTree: merkleTree,
        leafNodes: leafNodes,
      };
    },
    getMerkleProof(address, leafNodes, merkleRoot, merkleTree) {
      // Find index of whitelisted address of the connected wallet address, this is required because need to know which leafNode to get
      const addressIndex = Object.keys(this.$getWl()).indexOf(address);

      // If addressIndex is -1, means the connected wallet address is not whitelisted
      if (addressIndex < 0) {
        return null;
      }

      //Gget leaf node of connected wallet address
      const claimingLeafNode = leafNodes[addressIndex];
      // Get merkle proof of connected wallet address
      const hexProof = merkleTree.getHexProof(claimingLeafNode);

      // Check if the merkle proof is valid based on the merkle root generated in generateMerkleTree()
      const isProofVerified = merkleTree.verify(hexProof, claimingLeafNode, merkleRoot);

      // If merkle proof is invalid, means the connected wallet address is not whitelisted
      if (!isProofVerified) {
        return null;
      }

      return hexProof;
    },
    mint() {
      this.notifyError('');
      if (this.isPreSaleLive) {
        this.preSaleMint();
      } else if (this.isPublicSaleLive) {
        // Validate reCAPTCHA first
        this.setIsMinting(true);
        if (this.reCAPTCHAV2Token === '') {
          this.recaptchaV3();
        } else {
          this.validateReCaptcha('v2', this.reCAPTCHAV2Token);
        }
      }
    },
    async preSaleMint() {
      const { merkleRoot, merkleTree, leafNodes } = this.generateMerkleTree();
      if (this.isWhitelisted) {
        const merkleProof = this.getMerkleProof(
          this.getActiveAccountChecksum,
          leafNodes,
          merkleRoot,
          merkleTree
        );

        if (merkleProof) {
          this.setIsMinting(true);
          try {
            let gastimate = Math.floor(
              parseInt(
                await this.getIOContract.estimateGas.preSaleBuy(
                  merkleProof,
                  this.getAccountData.allowedMintQuantity,
                  this.mintQuantity,
                  {
                    value: ethers.utils.parseEther(this.price.toString()),
                  }
                )
              ) * 1.4
            );

            const tx = await this.getIOContract.preSaleBuy(
              merkleProof,
              this.getAccountData.allowedMintQuantity,
              this.mintQuantity,
              {
                value: ethers.utils.parseEther(this.price.toString()),
                gasLimit: gastimate,
              }
            );

            this.postMint('Pre-sale', tx);
          } catch (error) {
            this.handleMintError(error);
          }
        } else {
          this.notifyError('Invalid Proof');
        }
      } else {
        this.notifyError('Not in Whitelist');
      }
    },
    async publicSaleMint() {
      let signature;
      try {
        const signatureResponse = await axios.post(
          `${process.env.VUE_APP_API_URL}generate-signature`,
          {
            address: this.getActiveAccountChecksum,
          }
        );
        signature = signatureResponse.data.data.signature;
      } catch (error) {
        if (error.response.status === 400) {
          this.setIsMinting(false);
          this.notifyError(`Signature Error: ${error.response.data.data.message}`);
        }
      }

      try {
        let gastimate = Math.floor(
          parseInt(
            await this.getIOContract.estimateGas.publicSaleBuy(signature, {
              value: ethers.utils.parseEther(this.getDAData.price.toString()),
            })
          ) * 1.4
        );

        const tx = await this.getIOContract.publicSaleBuy(signature, {
          value: ethers.utils.parseEther(this.getDAData.price.toString()),
          gasLimit: gastimate,
        });

        this.postMint('Dutch Auction', tx);
      } catch (error) {
        this.handleMintError(error);
      }
    },
    async postMint(type, tx) {
      try {
        await tx.wait();
        this.$emit('mint-success');
        this.setIsMinting(false);
      } catch (error) {
        this.setIsMinting(false);
        this.notifyError(`${type} Mint Error: ${error.reason || error.message}`);
      }
    },
    handleMintError(error) {
      let errorMsg;

      if (error.error) {
        if (
          error.error.data &&
          error.error.data.originalError &&
          error.error.data.originalError.data
        ) {
          const scInterface = new ethers.utils.Interface(this.getIOAbi);
          const errorFragment = scInterface.getError(error.error.data.originalError.data);
          errorMsg = ErrorDict[errorFragment.name];
        } else if (error.error.code && error.error.code === -32603) {
          errorMsg = ErrorDict[error.error.message.replace('execution reverted: ', '')];
        }
      } else if (error.code && error.code === 4001) {
        errorMsg = error.message.replace('MetaMask Tx Signature: ', '');
      }

      this.setIsMinting(false);
      this.notifyError(errorMsg || error.reason || error.message);
      if (this.isPublicSaleLive) {
        this.resetReCAPTCHA('v2');
      }
    },
    async recaptchaV3() {
      await this.$recaptchaLoaded();
      const token = await this.$recaptcha('PUBLIC_MINT');

      this.validateReCaptcha('v3', token);
    },
    async validateReCaptcha(version, token) {
      // Validate token server side
      try {
        const validateReCaptchaResponse = await axios.post(
          `${process.env.VUE_APP_API_URL}validate-recaptcha`,
          {
            token,
            version,
          }
        );
        const response = validateReCaptchaResponse.data;

        if (response.data.success) {
          if (version === 'v3') {
            if (
              parseFloat(response.data.score) >
              parseFloat(process.env.VUE_APP_RECAPTCHA_V3_PUBLIC_MINT_THRESHOLD)
            ) {
              this.publicSaleMint();
            } else {
              this.showReCAPTCHAV2 = true;
              this.setIsMinting(false);
            }
          } else if (version === 'v2') {
            this.publicSaleMint();
          }
        } else {
          this.resetReCAPTCHA(version);
          this.notifyError(
            error.response.status === 400
              ? `reCAPTCHA Error: ${error.response.data.data.message}`
              : this.mintUnsuccessfulReCAPTCHAText
          );
          this.setIsMinting(false);
        }
      } catch (error) {
        this.resetReCAPTCHA(version);
        this.notifyError(
          error.response.status === 400
            ? `reCAPTCHA Error: ${error.response.data.data.message}`
            : this.mintUnsuccessfulReCAPTCHAText
        );
        this.setIsMinting(false);
      }
    },
    resetReCAPTCHA(version) {
      if (version === 'v2' && typeof this.$refs.recaptchaV2 !== 'undefined') {
        this.reCAPTCHAV2Token = '';
        this.$refs.recaptchaV2.reset();
      }
    },
    onReCAPTCHAV2Verified(recaptchaToken) {
      // Invoke reCAPTCHA v2 challenge
      this.reCAPTCHAV2Token = recaptchaToken;
    },
    onReCAPTCHAV2Expired() {
      // Reset reCAPTCHA v2 instance
      this.refs.recaptchaV2.reset();
      this.notifyError(this.mintUnsuccessfulReCAPTCHAText);
    },
  },
  async mounted() {
    if (this.isPublicSaleLive) {
      await this.storeDAData();
      this.daPrice = this.getDAData.price;

      // Init background poller
      setInterval(async (_) => {
        await this.storeDAData();
        this.daPrice = this.getDAData.price;
      }, 1000);
    }

    if (this.isWhitelisted) {
      this.mintQuantity = this.mintBalance;
    }
  },
  watch: {
    'getAccountData.hasPopulated': function (hasPopulated) {
      if (hasPopulated) {
        if (this.isPreSaleLive) {
          this.mintQuantity =
            this.getAccountData.allowedMintQuantity - this.getAccountData.mintedQuantity;
        } else if (this.isPublicSaleLive) {
          this.daPrice = this.getDAData.price;
        }
      }
    },
  },
};
</script>

<style lang="scss">
.sale__title {
  font-family: $font-primary;
  color: $white;
  font-size: 3rem;
  font-weight: 900;
  line-height: 1;
  text-transform: uppercase;

  @include small-mobile {
    font-size: 2.125rem;
  }

  @include tablet {
    font-size: 4rem;
  }

  @include desktop {
    font-size: 4.5rem;
  }

  @include wide-desktop {
    font-size: 7rem;
  }

  @include big {
    font-size: 9.5rem;
  }

  .mint-details__container {
    font-family: $font-primary;
    font-size: 1.063rem;
    text-transform: uppercase;
    margin-bottom: 10px;

    > div {
      line-height: 1.5;

      span {
        display: inline;

        @include mobile {
          font-size: 1rem;
        }

        @include hd {
          font-size: 1.375rem;
        }

        @include big {
          font-size: 2rem;
        }
      }
    }

    span.no-bold {
      font-weight: 400;
    }
  }

  span {
    display: block;
    position: relative;

    &.inline {
      display: inline-block;
    }
  }
}

.sale__supply,
.sale__price {
  font-size: 1.5rem;
}

.sale__price {
  font-weight: 700;
  text-transform: uppercase;

  @include mobile() {
    font-size: 1.25rem;
  }
}

.mint__container {
  display: flex;
  align-items: center;
  margin: 20px 0;

  .v-btn--mint {
    background: $black;
    color: $white;
    padding: 34px !important;
    font-size: 2rem;
    font-weight: 700;
    border-radius: 50px;
    min-width: 200px !important;
  }
}

.sale__quantity {
  display: flex;
  align-items: center;
  margin-right: 50px;

  * {
    margin: 0 10px;

    &:first-child {
      margin-left: 0;
    }
    &:last-child {
      margin-right: 0;
    }
  }

  .minus,
  .plus {
    font-size: 2rem;
    color: $white !important;

    &:focus::after {
      opacity: 0;
    }

    &.is-disabled {
      color: $grey-2 !important;
      cursor: not-allowed;
    }
  }

  .minus {
    transform: rotate(180deg);
  }

  .quantity {
    padding: 10px;
    background: $white;
    color: $black;
    font-size: 2rem;
    font-weight: 700;
    border-radius: 50px;
    width: 125px;
    text-align: center;
  }
}

.v-btn--home {
  margin-right: auto;
  width: auto;

  @include mobile {
    margin-left: auto;
  }
}

.recaptcha-v2 {
  margin-left: 20px;
  text-align: center;

  @include mobile {
    margin-top: 20px;
  }

  .recaptcha-v2__error {
    font-weight: 400;
    font-size: 0.875rem;
    text-transform: uppercase;
  }
}

.is-vertical {
  .sale__supply {
    margin-bottom: 20px;
  }

  .mint__container {
    flex-direction: column;

    .sale__quantity {
      margin-right: 0;
      margin-bottom: 20px;

      .minus,
      .plus,
      .quantity {
        font-size: 1.5rem;
      }

      .quantity {
        padding: 5px 10px;
      }
    }

    .v-btn--mint {
      font-size: 1.5rem;
      padding: 20px !important;
    }
  }
}
</style>
